/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button, IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable } from 'mobx';
import * as React from 'react';
import { ICittaStockistsData } from './citta-stockists.data';
import { ICittaStockistsProps } from './citta-stockists.props.autogenerated';

export interface ICittaStockistsState {
    allStockists?: [];
    countrySelected: string;
    stateorcitySelected: string;
    searchString: string;
    countryList: [];
    stateList: [];
    stockistList: [];
}

export interface ICittaStockistsViewProps extends ICittaStockistsProps<ICittaStockistsData> {
    stockistMain: INodeProps;
    stockistGrid: INodeProps;
    StockistContainer: IModuleProps;
    inputWrap: INodeProps;
    stockistTop: INodeProps;
    inputThirds: INodeProps;
    createStockistsAddress: React.ReactNode;
    createCountryDropDown: React.ReactNode;
    createCityStateDropDown: React.ReactNode;
    createSearchAddress: React.ReactNode;
}

/**
 *
 * CittaStockists component
 * @extends {React.PureComponent<ICittaStockistsProps<ICittaStockistsData>>}
 */
class CittaStockists extends React.PureComponent<ICittaStockistsProps<ICittaStockistsData>> {
    public state: ICittaStockistsState = {
        allStockists: [],
        countrySelected: '',
        stateorcitySelected: '',
        searchString: '',
        countryList: [],
        stateList: [],
        stockistList: [],
    };

    @observable private _countryList: string[] = [];
    @observable private _stateList: string[] = [];
    @observable private _stockistList: any[] | undefined;

    private _countrySelectRef: React.RefObject<HTMLSelectElement>;
    private _stateorcitySelectRef: React.RefObject<HTMLSelectElement>;
    private _searchInputRef: React.RefObject<HTMLInputElement>;

    public constructor(props: ICittaStockistsProps<ICittaStockistsData>) {
        super(props);
        this._countrySelectRef = React.createRef();
        this._stateorcitySelectRef = React.createRef();
        this._searchInputRef = React.createRef();
    }

    public async componentDidMount(): Promise<void> {
        await this._getAllStockists().catch(err => console.log(err));
        await this._getCountryList().catch(err => console.log(err));
        await this._getStateProvinces('').catch(err => console.log(err));
        await this._getStockistsList().catch(err => console.log(err));
    }
    public render(): JSX.Element {
        const viewProps = {
            ...this.props,
            StockistContainer: {
                moduleProps: this.props,
                className: classnames('infoSection')
            },
            stockistTop: {
                tag: 'div' as NodeTag,
                className: classnames('stockistTop', 'inputThirds')
            },
            stockistMain: {
                tag: 'div' as NodeTag,
                className: classnames('stockistMain')
            },
            stockistGrid: {
                tag: 'div' as NodeTag,
                className: classnames('stockistGrid')
            },
            createCityStateDropDown: this._renderCityStateDropDown(),
            createCountryDropDown: this._renderCountryDropDown(),
            createStockistsAddress: this._renderStockistsAddress(),
            createSearchAddress: this._renderSearchAddress(),
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }
    private _renderSearchAddress(): React.ReactNode {
        const searchPlaceholder = this.props && this.props.resources && this.props.resources.searchPlaceholder ? this.props.resources.searchPlaceholder : 'Search by Address';
        return (
            <form className='stockists-form inputWrap inputFW addressSearch' >
                <div className='searchText'>
                    <div className='c-defInput'>
                        <input
                            ref={this._searchInputRef}
                            type='text'
                            aria-label='Address Search'
                            autoComplete='off'
                            className='addressSearch'
                            placeholder={searchPlaceholder}
                            onChange={this._changeSearchText(this._searchInputRef)}
                        />
                    </div>
                </div>
                <div className='stockistsearch-submit'>
                    <Button
                        className='stockistsearch-submit-button'
                        title={'Search'}
                        color='primary'
                        onClick={this._handleSearch}
                        aria-label='Search'
                    />
                </div>
            </form>
        );
    }
    private _handleSearch = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const searchStr = (this._searchInputRef && this._searchInputRef.current && this._searchInputRef.current.value) ? this._searchInputRef.current.value : '';
        const countryValue = (this._countrySelectRef && this._countrySelectRef.current && this._countrySelectRef.current.value) ? this._countrySelectRef.current.value : '';
        const stateValue = (this._stateorcitySelectRef && this._stateorcitySelectRef.current && this._stateorcitySelectRef.current.value) ? this._stateorcitySelectRef.current.value : '';
        try {
            this._getStockistsList(countryValue, stateValue, searchStr).catch(err => console.log(err));
        } catch (error) {
            console.log(error);
        }
    }
    private _changeSearchText = (ref: React.RefObject<HTMLInputElement>) => (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState(
            { searchString: e.target.value }
        );
    }
    private _renderCityStateDropDown(): React.ReactNode {
        const citaAndStatePlaceholder = this.props && this.props.resources && this.props.resources.citaAndStatePlaceholder ? this.props.resources.citaAndStatePlaceholder : 'All Cities/States';
        return (
            <div className='inputWrap'>
                <div className='c-defSelWrap'>
                    <select
                        ref={this._stateorcitySelectRef}
                        className='country-picker'
                        aria-label='State or City'
                        name='stateorcity'
                        id='stateorcityselect'
                        value={this.state.stateorcitySelected}
                        onChange={this._onStateorCityChange}
                    >
                        <option value='' selected={true} aria-selected={true}>{citaAndStatePlaceholder}</option>
                        {this._renderStateorCityList()}
                    </select>
                </div>
            </div>
        );
    }
    private _renderCountryDropDown(): React.ReactNode {
        const countryPlaceholder = this.props && this.props.resources && this.props.resources.countryPlaceholder ? this.props.resources.countryPlaceholder : 'All Countries';
        return (
            <div className='inputWrap'>
                <div className='c-defSelWrap'>
                    <select
                        ref={this._countrySelectRef}
                        className='country-picker'
                        aria-label='Countries'
                        name='countries'
                        id='countryselect'
                        value={this.state.countrySelected}
                        onChange={this._onCountryChange}
                    >
                        <option value='' selected={true} aria-selected={true}>{countryPlaceholder}</option>
                        {this._renderCountryList()}
                    </select>
                </div>
            </div>
        );
    }
    private _onStateorCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ stateorcitySelected: e.target.value || '' });
        this._getStockistsList(this.state.countrySelected, e.target.value).catch(err => console.log(err));
    }
    private _renderStateorCityList(): JSX.Element[] | null {
        const stateorcity = this.state.stateList;
        if (stateorcity) {
            return stateorcity.map((stateorcityItem) =>
                this._getCities(stateorcityItem)
            );
        } else {
            return null;
        }
    }
    private _getCities(stateorcityItem: string): JSX.Element {
        return <option value={stateorcityItem} selected={false} aria-selected={false}>{stateorcityItem}</option>;
    }
    private _renderCountryList(): JSX.Element[] | null {
        const country = this.state.countryList;
        if (country) {
            return country.map((countryItem: string) =>
                this._getCountries(countryItem)
            );
        } else {
            return null;
        }
    }
    private _getCountries(countryItem: string): JSX.Element {
        return <option value={countryItem} selected={false} aria-selected={false}>{countryItem}</option>;
    }
    private _onCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const countryValue = e.target.value || '';
        this.setState({
            countrySelected: countryValue,
            stateorcitySelected: '' // Reset city/state when country changes
        });
        this._getStateProvinces(countryValue).catch(err => console.log(err));
        this._getStockistsList(countryValue, '', '').catch(err => console.log(err)); // Pass empty string for city/state
    }
    private _renderStockistsAddressBlock(stockistList: any): React.ReactNode {

        if (stockistList) {
            return stockistList.map((stockist: any) =>
            (
                <div className='col-auto col-sm-4 col-md-4 col-lg-4'>
                    <div className='ms-content-block citta-content-placement-item-11 font-light ms-content-block__details'>
                        <div className='ms-content-block__text'>
                            <h5 className='font-light'>{stockist.Store}</h5>
                        <p>
                            {stockist.StreetNumber} {stockist.Street}<br />
                            {stockist.Suburb || ''} <br />
                            {stockist.City} {stockist.PostalCode} <br />
                            {stockist.State} {stockist.CountryName} <br />
                        </p>
                        </div>
                    </div>
                </div>
            )
            );
        } else {
            return null;
        }
    }

    private _renderStockistsAddress(): React.ReactNode {
        const lists = this.state.stockistList || [];
        const countrySelected = this.state.countrySelected;

        // If "All Countries" is selected (empty), group by country
        if (!countrySelected && lists.length > 0) {
            const configCountry = this.props.config.country || 'NZ';
            const groupedByCountry = this._groupStockistsByCountry(lists, configCountry);
            return (
                <>
                    {groupedByCountry.map((group, idx) => (
                        <div className="col-12" key={idx}>
                            <div className='landing-page ms-text-block font-light alignment__center' style = {{ marginBottom: "48px"}}>
                                <h3>{group.name}</h3>
                            </div>
                            <div className="default-container-3-slots default-container-3-slots__3 fluid text-no-content-row">
                                <div className='row'>
                                    {this._renderStockistsAddressBlock(group.items)}
                                </div>
                            </div>
                        </div>
                    ))}
                </>
            );
        }

        // If specific country is selected, render without grouping
        return (
            <>
                {lists && lists.length > 0 ? (
                    <div className="col-12">
                        <div className="default-container-3-slots default-container-3-slots__3 fluid text-no-content-row">
                            <div className='row'>
                                {this._renderStockistsAddressBlock(lists)}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="col-12">
                        <p>No stockists found</p>
                    </div>
                )}
            </>
        );
    }

    private _groupStockistsByCountry(stockists: any[], configCountry: string): { name: string; items: any[] }[] {
        const grouped: { [key: string]: any[] } = {};
        
        stockists.forEach((stockist: any) => {
            const country = stockist.CountryName;
            // Group all non-NZ/AU countries as "International"
            const groupKey = (country === 'New Zealand' || country === 'Australia') ? country : 'International';
            
            if (!grouped[groupKey]) {
                grouped[groupKey] = [];
            }
            grouped[groupKey].push(stockist);
        });

        // Sort International group: US first, then others alphabetically
        if (grouped['International']) {
            grouped['International'].sort((a: any, b: any) => {
                if (a.CountryName === 'United States') {
                    return -1;
                }
                if (b.CountryName === 'United States') {
                    return 1;
                }
                // Add null check before calling localeCompare
                const countryA = a.CountryName || '';
                const countryB = b.CountryName || '';
                return countryA.localeCompare(countryB);
            });
        }

        // Map config country to group key
        const countryMap: { [key: string]: string } = {
            'NZ': 'New Zealand',
            'AU': 'Australia',
            'International': 'International'
        };
        const selectedCountryGroup = countryMap[configCountry] || 'New Zealand';

        // Return in order: selected country first, then others
        const order = [selectedCountryGroup, 'New Zealand', 'Australia', 'International'];
        const result: { name: string; items: any[] }[] = [];
        const addedGroups = new Set<string>();
        
        order.forEach(country => {
            if (grouped[country] && !addedGroups.has(country)) {
                result.push({ name: country, items: grouped[country] });
                addedGroups.add(country);
            }
        });

        return result;
    }

    private async _getCountryList(): Promise<void> {
        const allStockists = this.state.allStockists || [];
        let list: string[] = [];

        // Extract unique countries from allStockists
        allStockists.map((item: any) => {
            if (!list.includes(item.CountryName)) {
                list.push(item.CountryName);
            }
        });

        this._countryList = list;

        if (this._countryList) {
            this.setState({
                countryList: this._countryList
            });
        }
    }

    private async _getStateProvinces(countryCode: string): Promise<void> {
        const allStockists = this.state.allStockists || [];
        let list: string[] = [];

        // Filter stockists by country and extract unique states/cities
        const countryStockists = allStockists.filter((item: any) => !countryCode || item.CountryName === countryCode);

        countryStockists.map((item: any) => {
            // For New Zealand, use City; for others, use State
            const stateOrCity = item.CountryName === 'Australia' ? item.State : item.City;

            if (stateOrCity && !list.includes(stateOrCity)) {
                list.push(stateOrCity);
            }
        });

        this._stateList = list;
        if (this._stateList) {
            this.setState({
                stateList: this._stateList
            });
        }
    }

    private async _getAllStockists(): Promise<void> {
        const allStockists = [
            ...(this.props.config.stockistsAddressNZ || []),
            ...(this.props.config.stockistsAddressAU || []),
            ...(this.props.config.stockistsAddressInt || [])
        ];
        if (allStockists) {
            this.setState({
                allStockists: allStockists
            });
        }
    }

    private async _getStockistsList(countryCode?: string, cityorstate?: string, searchStr?: string): Promise<void> {
        let filteredList: any[] = [];
        // Get all stockists from config
        const allStockists = this.state.allStockists || [];

        // Filter based on country, city/state, and search string
        filteredList = allStockists.filter((stockist: any) => {
            const matchCountry = !countryCode || stockist.CountryName === countryCode;
            const matchCityState = !cityorstate || stockist.City === cityorstate || stockist.State === cityorstate;

            // Enhanced search: check multiple fields with lowercase comparison
            const searchLower = searchStr?.toLowerCase() || '';
            const matchSearch = !searchStr ||
                stockist.Store?.toLowerCase().includes(searchLower) ||
                stockist.StreetNumber?.toLowerCase().includes(searchLower) ||
                stockist.Street?.toLowerCase().includes(searchLower) ||
                stockist.Suburb?.toLowerCase().includes(searchLower) ||
                stockist.City?.toLowerCase().includes(searchLower) ||
                stockist.PostalCode?.toLowerCase().includes(searchLower) ||
                stockist.State?.toLowerCase().includes(searchLower);


            return matchCountry && matchCityState && matchSearch;
        });


        this._stockistList = filteredList;
        if (this._stockistList) {
            this.setState({
                stockistList: this._stockistList
            });
        }
    }
}
export default CittaStockists;
